import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { ERadioGroupType } from '@ui/sp-radio-group';

import type {
    EduLoginButtonEntity,
    EduLoginButtonItem,
    ExtendedPositionLegacy,
    WidgetPositionLegacy,
} from '@sites/entity-core';
import type { EduLoginButtonDefaultForm, EduLoginButtonForm, EduLoginButtonZeroForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { SpAnimationSettingsSignalLegacyComponent } from '@components/sp-animation-settings-signal-legacy';

import { EduLoginButtonSettingsComponent } from './edu-login-button-settings/edu-login-button-settings.component';

import type { OnInit } from '@angular/core';

/**
 * Editor settings panel for the `edu-login-button` widget — a fork of the button
 * control. Differs from the button control in two ways: the widget is a single,
 * fixed login button (there is no "add button" action), and the per-button
 * settings panel locks the link to `/courses/auth/login`.
 */
@Component({
    selector: 'web-edu-login-button-control',
    templateUrl: './edu-login-button-control.component.html',
    styleUrls: ['./edu-login-button-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        EduLoginButtonSettingsComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        SpExtendedPositionSignalSettingsComponent,
        SpAnimationSettingsSignalLegacyComponent,
    ],
})
export class EduLoginButtonControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<EduLoginButtonEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public readonly ERadioGroupType = ERadioGroupType;

    public readonly isDeviceDesktopSig = toSignal(this.layoutFacade.isDeviceDesktop$, { initialValue: true });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public form!: EduLoginButtonForm;

    public readonly editableButtonIndex = signal<number | null>(null);

    public ngOnInit(): void {
        this.destroyRef.onDestroy(() => {
            if (this.form?.inTransaction()) {
                this.form.rollbackTransaction();
            }
        });

        this.form = this.signalFormService.getOrCreateScoped<EduLoginButtonForm>(this.widget().id, this.destroyRef);
    }

    /** True when the widget lives inside a zero block — drives template branching. */
    public get isZero(): boolean {
        return this.widget().type === 'zero';
    }

    public get defaultForm(): EduLoginButtonDefaultForm {
        return this.form as EduLoginButtonDefaultForm;
    }

    public get zeroForm(): EduLoginButtonZeroForm {
        return this.form as EduLoginButtonZeroForm;
    }

    public editButton(index: number): void {
        // The settings panel edits the live form; the transaction makes
        // cancel/escape restore the pre-edit state.
        this.form.beginTransaction();
        this.editableButtonIndex.set(index);
    }

    public saveButtonSettings(): void {
        this.form.commitTransaction();
        this.editableButtonIndex.set(null);
    }

    public cancelButtonSettings(): void {
        this.form.rollbackTransaction();
        this.editableButtonIndex.set(null);
    }

    public getButtonSubText(button: EduLoginButtonItem): string {
        return button.link.url ?? '';
    }

    // --- Group layout ---

    public setHorizontalAlign(value: string): void {
        const align = value as EduLoginButtonEntity['data']['horizontalAlign'];

        this.form.transact(() => {
            if (this.form.isSynchronized()) {
                this.form.setMobileHorizontalAlign(align);
                this.form.setTabletHorizontalAlign(align);
            }
            this.form.setHorizontalAlign(align);
        });
    }

    public setSynchronizeStyle(): void {
        const horizontalAlign = this.form.horizontalAlign().value();

        this.form.transact(() => {
            this.form.setMobileHorizontalAlign(horizontalAlign);
            this.form.setTabletHorizontalAlign(horizontalAlign);
            this.form.setIsSynchronizeStyle(!this.form.isSynchronized());
        });
    }

    // --- Layout (default variant) ---

    public readonly defaultPosition = computed<WidgetPositionLegacy | null>(() => {
        const f = this.form as EduLoginButtonDefaultForm | undefined;
        if (!f || this.isZero) return null;
        return (f.position().value() as WidgetPositionLegacy) ?? null;
    });

    public onDefaultPositionChange(patch: Partial<WidgetPositionLegacy>): void {
        const current = this.defaultForm.position().value();
        this.defaultForm.setPosition({ ...current, ...patch });
    }

    // --- Layout (zero variant) ---

    public readonly zeroPosition = computed<ExtendedPositionLegacy | null>(() => {
        const f = this.form as EduLoginButtonZeroForm | undefined;
        if (!f || !this.isZero) return null;
        return f.position().value() as ExtendedPositionLegacy;
    });
}
